/**
 * Web Vitals Performance Tracking
 * 
 * Tracks Core Web Vitals and other performance metrics:
 * - LCP (Largest Contentful Paint)
 * - FID (First Input Delay)
 * - CLS (Cumulative Layout Shift)
 * - FCP (First Contentful Paint)
 * - TTFB (Time to First Byte)
 */

import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';

// Track Web Vitals and send to Google Analytics
const sendToAnalytics = (metric) => {
  if (!window.gtag) return;

  const { name, value, id, delta, rating } = metric;

  // Send to Google Analytics as custom event
  window.gtag('event', name, {
    event_category: 'Web Vitals',
    event_label: id,
    value: Math.round(name === 'CLS' ? value * 1000 : value), // CLS is multiplied by 1000 for better precision
    non_interaction: true,
    // Custom dimensions
    metric_name: name,
    metric_value: value,
    metric_delta: delta,
    metric_rating: rating
  });

  // Log to console for debugging
  console.log(`[Web Vitals] ${name}:`, {
    value: value.toFixed(2),
    rating,
    delta: delta.toFixed(2)
  });
};

// Initialize Web Vitals tracking
export const initWebVitals = () => {
  try {
    // Track Core Web Vitals
    getCLS(sendToAnalytics);
    getFID(sendToAnalytics);
    getFCP(sendToAnalytics);
    getLCP(sendToAnalytics);
    getTTFB(sendToAnalytics);

    console.log('Web Vitals tracking initialized');
  } catch (error) {
    console.error('Error initializing Web Vitals:', error);
  }
};

// Track page load time
export const trackPageLoadTime = () => {
  if (typeof window === 'undefined' || !window.performance) return;

  window.addEventListener('load', () => {
    const navigation = performance.getEntriesByType('navigation')[0];
    
    if (navigation) {
      const pageLoadTime = navigation.loadEventEnd - navigation.fetchStart;
      const domContentLoaded = navigation.domContentLoadedEventEnd - navigation.fetchStart;
      const timeToFirstByte = navigation.responseStart - navigation.fetchStart;

      // Send to Google Analytics
      if (window.gtag) {
        window.gtag('event', 'page_load_time', {
          event_category: 'Performance',
          page_load_time: Math.round(pageLoadTime),
          dom_content_loaded: Math.round(domContentLoaded),
          time_to_first_byte: Math.round(timeToFirstByte),
          page_path: window.location.pathname
        });
      }

      console.log('Page Load Metrics:', {
        pageLoadTime: `${Math.round(pageLoadTime)}ms`,
        domContentLoaded: `${Math.round(domContentLoaded)}ms`,
        timeToFirstByte: `${Math.round(timeToFirstByte)}ms`
      });
    }
  });
};

// Track resource load times
export const trackResourceTiming = () => {
  if (typeof window === 'undefined' || !window.performance) return;

  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    
    entries.forEach((entry) => {
      if (entry.entryType === 'resource') {
        const resourceLoadTime = entry.responseEnd - entry.startTime;
        
        // Track slow resources (> 1 second)
        if (resourceLoadTime > 1000 && window.gtag) {
          window.gtag('event', 'slow_resource', {
            event_category: 'Performance',
            resource_name: entry.name,
            resource_load_time: Math.round(resourceLoadTime),
            resource_type: entry.initiatorType
          });
        }
      }
    });
  });

  try {
    observer.observe({ entryTypes: ['resource'] });
  } catch (error) {
    console.error('Error observing resource timing:', error);
  }
};

export default {
  initWebVitals,
  trackPageLoadTime,
  trackResourceTiming
};

